// db.js=> v6.0.39

import imageHelper from './imageHelper.js';
import Storage from './storage.js';
import Logger from './logger.js';
import events from './event.js';

// Dessa Map-objekt lever bara i minnet och rensas när sidan laddas om.
// Metadatan överlever i Storage.js.
const fileCache = new Map();
const blobUrlCache = new Map();

class MCPStorageDB {

    constructor() {

    }

    async _canBrowserRenderHeic() {
        if (MCPStorageDB._heicSupport !== null) {
            return MCPStorageDB._heicSupport;
        }

        try {
            // Den moderna, korrekta metoden
            if (window.ImageDecoder && typeof ImageDecoder.isTypeSupported === 'function') {
                MCPStorageDB._heicSupport = await ImageDecoder.isTypeSupported('image/heic');
                Logger.debug('HEIC support (ImageDecoder):', MCPStorageDB._heicSupport);
                return MCPStorageDB._heicSupport;
            }
        } catch (e) {
            Logger.error('Error checking ImageDecoder (kan hända i vissa miljöer):', e);
        }

        // Fallback: Om API:t saknas (äldre webbläsare), anta "Nej" för säkerhets skull.
        Logger.debug('ImageDecoder API not available, assuming no HEIC blob support.');
        MCPStorageDB._heicSupport = false;
        return false;
    }

    /**
     * Hämtar bild-indexet från Storage.js
     * @returns {Object} - Ett objekt med bild-IDn som nycklar.
     */
    _getImagesIndex() {
        return Storage.get('mscp_images_index') || {};
    }

    /**
     * Sparar bild-indexet till Storage.js
     * @param {Object} index - Bild-indexet som ska sparas.
     */
    _saveImagesIndex(index) {
        Storage.save('mscp_images_index', index);
    }

    /**
     * Privat hjälp-metod: Rensar en bild från minnes-cachen.
     * @param {number | string} id - Bildens ID.
     */
    _revokeMemoryCache(id) {
        const url = blobUrlCache.get(id);
        if (url) {
            URL.revokeObjectURL(url);
        }
        fileCache.delete(id);
        blobUrlCache.delete(id);
    }

    /**
     * Genererar ett unikt ID.
     * @returns {number}
     */
    generateUniqueId() {
        return Date.now() + Math.floor(Math.random() * 10000);
    }

    /**
     * Sparar en bildfil.
     * Filen sparas i minnes-cachen.
     * Metadata sparas i Storage.js (sessionen).
     * @param {*} file - Filobjektet.
     * @param {*} data - Ytterligare data (t.ex. uploadStatus).
     * @returns {Promise<fileData | null>}
     */
    async save(file, data = {}) {
        return new Promise(async (resolve, reject) => {
            // Validera filtyp
            const validImageTypes = window.mscp?.validImageTypes;
            if (validImageTypes && !validImageTypes.includes(file.type)) {
                Logger.error('Invalid image type %s', file.type);
                reject(false);
                return;
            }
            const imageResolution = await imageHelper.getImageResolution(file);
            let isHeic = false;
            let blobUrl = null; // Starta som null

            if (!imageResolution || !imageResolution.width || !imageResolution.height) {
                if (file.type === 'image/heic' || file.name?.toLowerCase().endsWith('.heic')) {
                    Logger.debug('HEIC image detected, checking browser support...');
                    data.uploadStatus = 'converting';
                    isHeic = true;
                } else {
                    Logger.error('Invalid image resolution:', imageResolution);
                    reject(false); return;
                }
            }
            Logger.debug('Image resolution:', imageResolution);

            // Ta bort ALLA gamla bilder från minne och Storage.js
            await this.clearAllImages(false);

            // Skapa ID och Blob URL
            const id = this.generateUniqueId();
            fileCache.set(id, file);

            // 1. Spara i minnes-cachen om möjligt
            let createBlob = false;
            if (!isHeic) {
                createBlob = true; // Alltid för JPG/PNG
            } else {
                // Om det ÄR HEIC, kolla webbläsarens stöd
                if (await this._canBrowserRenderHeic()) {
                    Logger.debug('Browser supports HEIC, creating blobUrl.');
                    createBlob = true; // Safari
                } else {
                    Logger.debug('Browser does NOT support HEIC, skipping blobUrl.');
                    createBlob = false; // Chrome
                }
            }

            if (createBlob) {
                try {
                    blobUrl = URL.createObjectURL(file);
                    blobUrlCache.set(id, blobUrl);
                } catch (e) {
                    Logger.error('Kunde inte skapa blob-url:', e);
                    blobUrl = null;
                }
            }

            // 2. Förbered metadata för Storage.js
            const fileData = {
                id: id,
                imageResolution: imageResolution,
                uploadStatus: data.uploadStatus || 'pending',
                blobUrl: blobUrl,
                ...data
            };

            // 3. Spara metadata i Storage.js
            const index = this._getImagesIndex();
            index[id] = fileData;
            this._saveImagesIndex(index);

            Logger.log('File saved in memory cache:', fileData);
            Storage.save('activeImageID', fileData.id);

            resolve(fileData);
        });
    }

    /**
     * Hämtar bilddata (metadata + fil) för ett specifikt ID.
     * @param {number} id - Bildens ID.
     * @returns {Promise<fileData | null>}
     */
    async getImageData(id) {
        if (!id) {
            Logger.debug('No image ID provided for image retrieval');
            return Promise.resolve(null);
        }

        return new Promise((resolve) => {
            const index = this._getImagesIndex();
            const fileData = index[id]; // Hämta metadata från Storage

            if (fileData) {
                // Åter-populera filobjektet och blobUrl från minnes-cachen
                fileData.file = fileCache.get(id);
                fileData.blobUrl = blobUrlCache.get(id);
                resolve(fileData);
            } else {
                resolve(null);
            }
        });
    }

    /**
     * Klonar metadata för en bild till nytt ID.
     * @param {number} oldId - Ursprungligt bild-ID.
     * @param {number} newId - Nytt bild-ID.
     * @returns {Promise<boolean>}
     */
    async cloneImageMeta(oldId, newId) {
        if (!oldId || !newId) {
            Logger.error('Missing oldId or newId for cloneImageMeta');
            return Promise.reject(false);
        }

        return new Promise((resolve, reject) => {
            const index = this._getImagesIndex();
            const oldData = index[oldId];

            if (!oldData) {
                Logger.error('Image not found in session storage for clone:', oldId);
                reject(false);
                return;
            }

            const newData = {
                ...oldData,
                id: newId
            };

            if (newData.serverUrl) {
                newData.serverUrl = newData.serverUrl.replace(
                    new RegExp(String(oldId), 'g'),
                    String(newId)
                );
            }

            index[newId] = newData;
            this._saveImagesIndex(index);

            const oldFile = fileCache.get(oldId);
            if (oldFile) {
                fileCache.set(newId, oldFile);
            }
            const oldBlobUrl = blobUrlCache.get(oldId);
            if (oldBlobUrl) {
                blobUrlCache.set(newId, oldBlobUrl);
            }

            Logger.debug('Image metadata cloned in session storage:', { oldId, newId });
            resolve(true);
        });
    }

    /**
     * Uppdaterar metadata för en bild.
     * @param {number} id - Bildens ID.
     * @param {Object} data - Datan som ska uppdateras.
     * @returns {Promise<boolean>}
     */
    async updateImageData(id, data) {
        if (!id || !data) {
            Logger.error('No image ID or data provided for update');
            return Promise.reject(false);
        }

        return new Promise((resolve, reject) => {
            const index = this._getImagesIndex();
            const fileData = index[id];

            if (fileData) {
                // Slå ihop ny data
                Object.assign(fileData, data);

                // OBS: revoka INTE blob-url:en här vid 'uploaded'. Foto-lagret (<img>)
                // BYTS visserligen till serverUrl — men via cropImageViewport
                // updateImageSrc(), som först FÖRLADDAR serverUrl (async, ett
                // nätverksanrop) och byter src först på tempImg.onload. Revoken här är
                // däremot omedelbar. Det ger en RACE: mellan revoke och det klara
                // src-bytet har <img> fortfarande sin (nu revokade) blob-src. En reflow
                // i det gapet (t.ex. margin/pappersval i steg 2) får iOS WebKit att
                // av-koda om från den döda blobben → bilden "försvinner". Desktop
                // av-kodar inte om → syns. Blob-vägen finns bara på Safari/HEIC
                // (createBlob), därav iOS-only. Genom att inte revoka här hålls blobben
                // giltig tills bytet hunnit klart; den frigörs ändå vid
                // deleteImage()/clearAllImages() när bilden byts ut eller tas bort.

                // Spara uppdaterad metadata
                this._saveImagesIndex(index);
                Logger.debug('Image updated in session storage:', fileData);
                resolve(true);

            } else {
                Logger.error('Image not found in session storage:', id);
                reject(false);
            }
        });
    }

    /**
     * Raderar en bild (från minne och metadata).
     * @param {*} id - Bildens ID.
     * @returns {Promise<boolean>}
     */
    async deleteImage(id) {
        if (!id) {
            Logger.error('No image ID provided for deletion');
            return Promise.reject(false);
        }
        return new Promise((resolve, reject) => {
            const index = this._getImagesIndex();
            if (index[id]) {
                // Ta bort från metadata
                delete index[id];
                this._saveImagesIndex(index);

                // Ta bort från minnet
                this._revokeMemoryCache(id);

                Logger.debug('File deleted from session and memory:', id);
                resolve(true);
            } else {
                Logger.error('Error deleting file: Not found', id);
                reject(false);
            }
        });
    }

    /**
     * Hämtar alla bilder (metadata + fil).
     * Används av uploadHandler.
     * @returns {Promise<fileData[]>}
     */
    async getAllImages() {
        return new Promise((resolve) => {
            const index = this._getImagesIndex();
            const allImagesData = Object.values(index);

            // Åter-populera filobjektet från minnes-cachen
            allImagesData.forEach(imgData => {
                imgData.file = fileCache.get(imgData.id);
            });

            resolve(allImagesData);
        });
    }

    /**
     * Rensar alla bilder (från minne och metadata).
     * @returns {Promise<boolean>}
     */
    async clearAllImages(log = true) {
        return new Promise((resolve) => {
            const index = this._getImagesIndex();

            // Iterera och rensa alla från minnet
            Object.keys(index).forEach(id => {
                this._revokeMemoryCache(Number(id));
            });

            // Töm metadata-indexet
            this._saveImagesIndex({});

            if (log) {
                Logger.info('All files deleted from session and memory');
            }
            resolve(true);
        });
    }

    /**
     * Räknar antalet bilder i metadatan.
     * @returns {Promise<number>}
     */
    async getImageCount() {
        return new Promise((resolve) => {
            const index = this._getImagesIndex();
            resolve(Object.keys(index).length);
        });
    }

}

// Exportera en instans
const db = new MCPStorageDB();
export default db;